import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { TaskTypeSchema } from './use-load-task-types';

export const CreateTaskTypeVariablesSchema = z.object({
  name: z.string().min(1),
  defaultTitle: z.string().nullish(),
  defaultDescription: z.string().nullish(),
  defaultDueOffsetDays: z.number().int().nonnegative().nullish(),
});

export type CreateTaskTypeVariables = z.infer<typeof CreateTaskTypeVariablesSchema>;

export async function createTaskTypeFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateTaskTypeVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.post(
    '/task_types',
    CreateTaskTypeVariablesSchema.parse(variables),
  );
  return TaskTypeSchema.parse(data);
}

export function useCreateTaskType() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<z.infer<typeof TaskTypeSchema>, ApiError, CreateTaskTypeVariables>({
    mutationFn: variables => createTaskTypeFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['taskTypes'] });
    },
  });

  return addActionNameToMutationResult<
    z.infer<typeof TaskTypeSchema>,
    ApiError,
    CreateTaskTypeVariables,
    'createTaskType'
  >('createTaskType', mutation);
}
